import { Flex, Form, Typography } from "antd"
import React, { type ReactNode } from "react"
import { useTranslation } from "react-i18next"

import { TextArea } from "../../../components/Input/TextArea.tsx"
import { TextInput } from "../../../components/Input/TextInput.tsx"
import SelectComponent from "../../../components/Select/index.tsx"
import { descriptionTextAreaProps } from "../helpers/reportFormFields.ts"
import {
  folderFromSelection,
  folderSelection,
  type ReportSettingsValues
} from "../helpers/reportSettings.ts"

const { Text } = Typography

// Matches the model's CharField, so an over-long name fails in the field
// instead of as a 400.
const NAME_MAX_LENGTH = 255

interface ReportGeneralFieldsProps {
  value: ReportSettingsValues
  onChange: (patch: Partial<ReportSettingsValues>) => void
  folderOptions: string[]
  nameError: boolean
  /** Only a modal may take the cursor on open; a full page must not. */
  autoFocusName?: boolean
  /** Folder and Tags side by side, for a host wide enough to hold both. */
  split?: boolean
  /** Rendered beside the folder picker, e.g. a New folder button. */
  folderAction?: ReactNode
}

/**
 * The four metadata fields a report is authored with, shared by the create
 * wizard's first step and the settings modal so the two cannot drift on copy,
 * presentation or the folder rule.
 *
 * Renders bare `Form.Item`s: each host owns its own `<Form>` and whatever else
 * sits alongside them.
 */
export const ReportGeneralFields = ({
  value,
  onChange,
  folderOptions,
  nameError,
  autoFocusName = false,
  split = false,
  folderAction
}: ReportGeneralFieldsProps) => {
  const { t } = useTranslation()

  return (
    <>
      <Form.Item
        required
        label={t("reports.wizard.general.name")}
        validateStatus={nameError ? "error" : undefined}
        help={nameError ? t("reports.wizard.general.nameRequired") : undefined}
      >
        <TextInput
          autoFocus={autoFocusName}
          value={value.name}
          maxLength={NAME_MAX_LENGTH}
          placeholder={t("reports.wizard.general.namePlaceholder")}
          onChange={(e) => onChange({ name: e.target.value })}
        />
      </Form.Item>

      <Form.Item label={t("reports.wizard.general.description")}>
        <TextArea
          {...descriptionTextAreaProps}
          value={value.description}
          placeholder={t("reports.wizard.general.descriptionPlaceholder")}
          onChange={(e) => onChange({ description: e.target.value })}
        />
      </Form.Item>

      <div
        className={
          split ? "grid grid-cols-1 gap-x-6 md:grid-cols-2" : undefined
        }
      >
        <Form.Item
          label={
            <Flex align="center" gap={4}>
              <span>{t("reports.wizard.general.folder")}</span>
              <Text type="secondary">
                {t("reports.wizard.general.optional")}
              </Text>
            </Flex>
          }
        >
          <Flex gap="small" align="center">
            <div className="min-w-0 flex-1">
              <SelectComponent
                allowClear
                mode="tags"
                // Folder grouping (reportFolders.ts) buckets by an exact,
                // case-sensitive string match — capitalizing "marketing" to
                // "Marketing" on retype would silently split one folder into two.
                disableAutoCapitalize
                disableLabelCapitalize
                aria-label={t("reports.wizard.general.folder")}
                value={folderSelection(value.folder)}
                placeholder={t("reports.wizard.general.folderPlaceholder")}
                options={folderOptions.map((folder) => ({
                  label: folder,
                  value: folder
                }))}
                onChange={(folders) =>
                  onChange({ folder: folderFromSelection(folders as string[]) })
                }
              />
            </div>
            {folderAction}
          </Flex>
        </Form.Item>

        <Form.Item label={t("reports.wizard.general.tags")}>
          <SelectComponent
            mode="tags"
            // Tags share the folder field's mechanics above (always-empty
            // `options`, so nothing typed is ever "known") and reportSettings.ts
            // diffs tags with an exact, case-sensitive `===` — retyping an
            // existing tag verbatim would otherwise commit it recapitalized
            // and register as an unintended change.
            disableAutoCapitalize
            aria-label={t("reports.wizard.general.tags")}
            value={value.tags}
            tokenSeparators={[",", " "]}
            placeholder={t("reports.wizard.general.tagsPlaceholder")}
            onChange={(tags) => onChange({ tags: tags as string[] })}
          />
        </Form.Item>
      </div>
    </>
  )
}
